export interface CallSourceDisplayConfig {
  color: string;
  label: string;
  priority: number;
}

export const CALL_SOURCE_COLORS = {
  web: '#ffaa66',
  guide: '#6699dd',
  mail: '#ffcc88',
};

const WEB_BASE_COLORS = [
  '#ffb88a',
  '#ffdd99',
  '#ffbb77',
  '#ffe0aa',
  '#ffeecc',
  '#ffd6b3',
];
const GUIDE_BASE_COLORS = [
  '#88bbff',
  '#aaddff',
  '#77aaee',
  '#99ccff',
  '#bbddff',
  '#b3d9ff',
];

const categorizeCallSource = (source: string): 'web' | 'guide' => {
  const lower = source.toLowerCase();
  if (lower.includes('web') || lower === 'mail') return 'web';
  return 'guide';
};

const getCallSourcePriority = (source: string): number => {
  const lower = source.toLowerCase();
  if (lower === 'web') return 1;
  if (lower === 'mail') return 2;
  if (lower.includes('web')) return 3;
  if (lower === 'guide') return 4;
  if (lower.includes('guide')) return 5;
  if (lower.includes('deprecated')) return 6;
  return 7;
};

const formatCallSourceLabel = (source: string): string => {
  const lower = source.toLowerCase();
  if (lower === 'web') return 'Appels Web';
  if (lower === 'guide') return 'Appels Guide';
  if (lower === 'mail') return 'Demandes mail';
  if (lower === 'web-test') return 'Web Test';
  if (lower.includes('deprecated') || lower.includes('other')) return 'Autres';
  if (lower.includes('pnc-july-2025')) return 'Guide PNC 07 2025';
  return source
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getColorForSource = (
  category: 'web' | 'guide',
  index: number,
): string => {
  const baseColors = category === 'web' ? WEB_BASE_COLORS : GUIDE_BASE_COLORS;
  const baseIndex = index % baseColors.length;
  return baseColors[baseIndex];
};

export const getCallSourceDisplayConfig = (
  sources: string[],
  includeMailSource = false,
): Record<string, CallSourceDisplayConfig> => {
  const allSources = includeMailSource ? [...sources, 'mail'] : sources;

  const sortedSources = [...allSources].sort((a, b) => {
    const priorityA = getCallSourcePriority(a);
    const priorityB = getCallSourcePriority(b);
    if (priorityA !== priorityB) return priorityA - priorityB;
    return a.localeCompare(b);
  });

  const webSources = sortedSources.filter(
    (source) => categorizeCallSource(source) === 'web',
  );
  const guideSources = sortedSources.filter(
    (source) => categorizeCallSource(source) === 'guide',
  );

  const config: Record<string, CallSourceDisplayConfig> = {};

  // Assign colors to web sources with special handling for 'web' and 'mail'
  let webColorIndex = 0;
  webSources.forEach((source) => {
    let color: string;

    if (source === 'web') {
      color = CALL_SOURCE_COLORS.web;
    } else if (source === 'mail') {
      color = CALL_SOURCE_COLORS.mail;
    } else {
      color = getColorForSource('web', webColorIndex);
      webColorIndex++;
    }

    config[source] = {
      color,
      label: formatCallSourceLabel(source),
      priority: getCallSourcePriority(source),
    };
  });

  // Assign colors to guide sources with separate indexing
  let guideColorIndex = 0;
  guideSources.forEach((source) => {
    let color: string;

    if (source === 'guide') {
      color = CALL_SOURCE_COLORS.guide;
    } else {
      color = getColorForSource('guide', guideColorIndex);
      guideColorIndex++;
    }

    config[source] = {
      color,
      label: formatCallSourceLabel(source),
      priority: getCallSourcePriority(source),
    };
  });

  return config;
};

export const getOrderedCallSources = (
  sources: string[],
  includeMailSource = false,
): string[] => {
  const allSources = includeMailSource ? [...sources, 'mail'] : sources;

  return [...allSources].sort((a, b) => {
    const priorityA = getCallSourcePriority(a);
    const priorityB = getCallSourcePriority(b);
    if (priorityA !== priorityB) return priorityA - priorityB;
    return a.localeCompare(b);
  });
};

export const getCallSourceColors = (sources: string[]): string[] => {
  const config = getCallSourceDisplayConfig(sources);
  const orderedSources = getOrderedCallSources(sources);
  return orderedSources.map((source) => config[source]?.color || '#ccc');
};

export const getCallSourceLabels = (
  sources: string[],
): Record<string, string> => {
  const config = getCallSourceDisplayConfig(sources);
  const labels: Record<string, string> = {};

  sources.forEach((source) => {
    labels[source] = config[source]?.label || formatCallSourceLabel(source);
  });

  return labels;
};
